import { useCallback, useEffect, useRef, useState } from "react";
import {
  MDBModal, MDBModalDialog, MDBModalContent, MDBModalHeader, MDBModalTitle,
  MDBModalBody, MDBModalFooter, MDBBtn
} from "mdb-react-ui-kit";
import "./MediaTrimmer.css";

import { doFetchJson } from "Projects/CC_cubi_clan_project/Utils/ConnectionManager";
import { mfmUrl } from "Projects/CC_cubi_clan_project/Utils/MfmService";
import { toast } from "Projects/CC_cubi_clan_project/GlobalComponents/ToastHost/ToastHost";

const MIN_DURATION = 3;

function formatTime(s) {
  if (s == null) return "--:--";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
}

export default function MediaTrimmer({ show, setShow, medio, onSaved }) {
  const barRef = useRef(null);
  const canvasRef = useRef(null);
  const audioRef = useRef(null);
  const [duration, setDuration] = useState(medio?.duracionOriginal || 60);
  const [trimStart, setTrimStart] = useState(
    medio?.trimInicio != null ? Number(medio.trimInicio) : 0
  );
  const [trimEnd, setTrimEnd] = useState(
    medio?.trimFin != null ? Number(medio.trimFin) : medio?.duracionOriginal || 60
  );
  const [saving, setSaving] = useState(false);
  const dragging = useRef(null); // "start" | "end"

  const isAudio = medio?.mediaType === "AUDIO" || medio?.rol === "AUDIO";
  const mediaUrl = medio?.mfmUuid ? mfmUrl(medio.mfmUuid) : null;

  // Waveform via OfflineAudioContext
  useEffect(() => {
    if (!isAudio || !mediaUrl || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    fetch(mediaUrl, { credentials: "include" })
      .then((r) => r.arrayBuffer())
      .then((buf) => {
        const offCtx = new OfflineAudioContext(1, 44100 * 60, 44100);
        return offCtx.decodeAudioData(buf).then((decoded) => {
          const data = decoded.getChannelData(0);
          const step = Math.ceil(data.length / canvas.width);
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.fillStyle = "#0d6efd";
          for (let i = 0; i < canvas.width; i++) {
            let max = 0;
            for (let j = 0; j < step; j++) {
              const idx = i * step + j;
              if (idx < data.length) max = Math.max(max, Math.abs(data[idx]));
            }
            const h = Math.max(1, max * canvas.height);
            ctx.fillRect(i, (canvas.height - h) / 2, 1, h);
          }
          setDuration(decoded.duration);
          if (medio?.trimFin == null) setTrimEnd(decoded.duration);
        });
      })
      .catch(() => {});
  }, [mediaUrl, isAudio]);

  // Drag handles
  const getBarPct = useCallback((clientX) => {
    if (!barRef.current) return 0;
    const rect = barRef.current.getBoundingClientRect();
    return Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
  }, []);

  const onMouseMove = useCallback((e) => {
    if (!dragging.current) return;
    const pct = getBarPct(e.clientX);
    const t = pct * duration;
    if (dragging.current === "start") {
      setTrimStart(Math.min(t, trimEnd - MIN_DURATION));
    } else {
      setTrimEnd(Math.max(t, trimStart + MIN_DURATION));
    }
  }, [duration, trimStart, trimEnd, getBarPct]);

  const onMouseUp = useCallback(() => { dragging.current = null; }, []);

  useEffect(() => {
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [onMouseMove, onMouseUp]);

  async function handleSave() {
    setSaving(true);
    try {
      const body = new URLSearchParams();
      body.append("trimInicio", trimStart.toFixed(3));
      body.append("trimFin", trimEnd.toFixed(3));
      const res = await doFetchJson("EXHI", `medios/${medio.idSeccionMedio}/trim`, {
        method: "PUT",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      if (res?.STATE === "SUCCESS") {
        toast.show({ message: "Trim guardado", type: "success" });
        onSaved && onSaved();
        setShow(false);
      } else {
        toast.show({ message: res?.MESSAGE || "Error al guardar trim", type: "danger" });
      }
    } catch {
      toast.show({ message: "Error de conexión", type: "danger" });
    } finally {
      setSaving(false);
    }
  }

  const handleClose = useCallback(() => setShow(false), [setShow]);

  const startPct = (trimStart / duration) * 100;
  const endPct = (trimEnd / duration) * 100;

  return (
    <MDBModal show={show} onHide={handleClose} tabIndex="-1">
      <MDBModalDialog>
        <MDBModalContent>
          <MDBModalHeader>
            <MDBModalTitle>Ajustar trim — {medio?.tituloMfm || "Medio"}</MDBModalTitle>
            <MDBBtn className="btn-close" color="none" onClick={() => setShow(false)} />
          </MDBModalHeader>
          <MDBModalBody>
            {isAudio && (
              <canvas ref={canvasRef} className="exhi-trimmer-waveform" width={600} height={60} />
            )}

            {/* Trim bar */}
            <div
              ref={barRef}
              className="exhi-trimmer-bar-wrap"
            >
              <div
                className="exhi-trimmer-filled"
                style={{ left: `${startPct}%`, width: `${endPct - startPct}%` }}
              >
                <div
                  className="exhi-trimmer-handle start"
                  onMouseDown={(e) => { e.preventDefault(); dragging.current = "start"; }}
                />
                <div
                  className="exhi-trimmer-handle end"
                  onMouseDown={(e) => { e.preventDefault(); dragging.current = "end"; }}
                />
              </div>
            </div>
            <div className="exhi-trimmer-time-labels">
              <span>Inicio: {formatTime(trimStart)}</span>
              <span>Duración: {formatTime(trimEnd - trimStart)}</span>
              <span>Fin: {formatTime(trimEnd)}</span>
            </div>

            {/* Mini-preview */}
            {mediaUrl && (
              <div className="exhi-trimmer-preview">
                {isAudio ? (
                  <audio
                    ref={audioRef}
                    src={`${mediaUrl}#t=${trimStart.toFixed(1)},${trimEnd.toFixed(1)}`}
                    controls
                    style={{ width: "100%" }}
                  />
                ) : (
                  <video
                    src={`${mediaUrl}#t=${trimStart.toFixed(1)}`}
                    controls
                    style={{ maxWidth: "100%", maxHeight: 180 }}
                  />
                )}
              </div>
            )}
          </MDBModalBody>
          <MDBModalFooter>
            <MDBBtn color="secondary" onClick={() => setShow(false)} disabled={saving}>Cancelar</MDBBtn>
            <MDBBtn onClick={handleSave} disabled={saving}>{saving ? "Guardando..." : "Guardar trim"}</MDBBtn>
          </MDBModalFooter>
        </MDBModalContent>
      </MDBModalDialog>
    </MDBModal>
  );
}
